<template>
  <div class="p-4 md:p-8 max-w-3xl mx-auto">
    
    <!-- Profile Card -->
    <el-card class="!rounded-xl !border-gray-200 shadow-sm overflow-visible" shadow="hover">
      <template #header v-if="!inDialog">
        <div class="flex items-center justify-between">
          <span class="text-lg font-bold text-gray-900">{{ $t('Account Status') }}</span>
          <el-tag :type="isSubscribed ? 'success' : 'info'" effect="dark" round size="large">
            {{ isSubscribed ? $t('Premium') : $t('Free') }}
          </el-tag>
        </div>
      </template>

      <!-- Loading State -->
      <div v-if="loading" class="py-12 flex flex-col items-center justify-center min-h-[200px]">
         <el-icon class="is-loading text-indigo-500 mb-3" :size="32"><Loading /></el-icon>
         <span class="text-gray-500 text-sm font-medium">{{ loadingMessage }}</span>
      </div>

      <!-- Main Content -->
      <div v-else class="flex flex-col md:flex-row gap-8 items-start py-4">
        
        <!-- Visual Status -->
        <div class="flex-shrink-0 mx-auto md:mx-0">
          <div class="w-32 h-32 rounded-full bg-gray-50 flex items-center justify-center border-4 border-white shadow-lg relative bg-gradient-to-b from-gray-50 to-gray-100">
             <div v-if="isSubscribed" class="absolute inset-0 rounded-full bg-gradient-to-br from-indigo-500/10 to-purple-600/10"></div>
             <img :src="isSubscribed ? VIP : NOVIP" class="w-20 h-20 object-contain z-10 drop-shadow-sm" alt="Status Icon" loading="lazy"/>
          </div>
        </div>

        <!-- Info Area -->
        <div class="flex-1 w-full">
           
           <!-- Subscribed View -->
           <div v-if="isSubscribed" class="animate-fade-in">
              <h2 class="text-2xl font-bold text-gray-800 mb-2">{{ translatedProductName || $t('Premium') }}</h2>
              
              <div class="bg-indigo-50 rounded-lg p-4 border border-indigo-100 mb-6">
                <div class="flex items-center gap-2 mb-1">
                  <el-icon class="text-indigo-600"><Calendar /></el-icon>
                  <span class="text-sm font-medium text-indigo-900">{{ $t('Valid until') }}</span>
                </div>
                <div class="text-lg font-mono text-indigo-700 font-bold ml-6">
                  {{ formattedExpiration }}
                </div>
              </div>

              <div class="flex flex-wrap gap-3">
                 <a v-if="update_payment_method_url" :href="update_payment_method_url" target="_blank" class="no-underline">
                    <el-button plain size="default">{{ $t('Update Payment') }}</el-button>
                 </a>
                 <a v-if="cancel_url" :href="cancel_url" target="_blank" class="no-underline">
                    <el-button type="danger" plain size="default">{{ $t('Manage Subscription') }}</el-button>
                 </a>
              </div>
           </div>

           <!-- Free View -->
           <div v-else class="animate-fade-in">
              <h2 class="text-xl font-bold text-gray-800 mb-2">{{ $t('Free Plan') }}</h2>
              <p class="text-gray-500 mb-6 leading-relaxed">{{ $t('Upgrade to unlock all features including custom masks, high-res exports, and more.') }}</p>
              
              <!-- Restore Purchase Section -->
              <div class="bg-gray-50 p-5 rounded-xl border border-gray-200">
                <div class="flex items-center gap-2 mb-3">
                  <el-icon class="text-gray-400"><Refresh /></el-icon>
                  <span class="text-xs font-bold text-gray-500 uppercase tracking-wider">{{ $t('Restore Purchase') }}</span>
                </div>
                
                <el-form :model="formData" ref="emailForm" :rules="rules" @submit.prevent class="restore-form">
                  <div class="flex flex-col sm:flex-row gap-3">
                    <el-form-item prop="email" class="!mb-0 flex-1">
                      <el-input 
                        v-model="formData.email" 
                        :placeholder="$t('Enter your purchase email')" 
                        type="email" 
                        :prefix-icon="Message"
                      />
                    </el-form-item>
                    <el-form-item class="!mb-0">
                      <el-button type="primary" :loading="loading" @click="validateAndGetData">
                        {{ $t('Restore') }}
                      </el-button>
                    </el-form-item>
                  </div>
                </el-form>
              </div>
           </div>

        </div>
      </div>
    </el-card>

    <div class="text-center mt-8 space-y-2">
       <p class="text-xs text-gray-400 font-mono">ID: {{ displayEmail }}</p>
       <p class="text-xs text-gray-300">Copyright © emperinter</p>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import { ElNotification, ElButton, ElCard, ElTag, ElIcon, ElInput, ElForm, ElFormItem } from 'element-plus';
import { Loading, Calendar, Refresh, Message } from '@element-plus/icons-vue';
import { productDaysMap, config } from '@/config';
import VIP from '@/assets/vip.png';
import NOVIP from '@/assets/novip.png';

export default {
  name: 'AccountPage',
  components: {
    ElButton, ElCard, ElTag, ElIcon, ElInput, ElForm, ElFormItem,
    Loading, Calendar, Refresh
  },
  data() {
    return {
      // Icons
      VIP, NOVIP, Message,
      
      // State
      loading: false,
      loadingType: '',
      userInfo: null,
      transactionInfo: null,
      isSubscribed: false,
      productName: '',
      expirationTime: null,
      
      // URLs
      cancel_url: "",
      update_payment_method_url: "",
      
      // Form
      formData: { email: '' },
      rules: {
        email: [
          { required: true, message: this.$t('EmailRequired'), trigger: 'blur' },
          { type: 'email', message: this.$t('VaildEmail'), trigger: ['blur', 'change'] }
        ]
      }
    };
  },
  computed: {
    translatedProductName() {
      return this.productName ? this.$t(this.productName) : '';
    },
    loadingMessage() {
      return this.loadingType === 'validating' ? this.$t('Validating...') : 
             this.loadingType === 'fetching' ? this.$t('Restoring purchases...') : 
             this.$t('Loading...');
    },
    formattedExpiration() {
      if (!this.expirationTime) return '';
      return new Date(this.expirationTime).toLocaleDateString() + ' ' + new Date(this.expirationTime).toLocaleTimeString();
    },
    displayEmail() {
      return this.formData.email || localStorage.getItem("email") || 'Guest';
    }
  },
  mounted() {
    this.refreshLocalState();
  },
  methods: {
    refreshLocalState() {
      this.expirationTime = localStorage.getItem("expirationTime");
      const storedEmail = localStorage.getItem("email");
      if (storedEmail) this.formData.email = storedEmail;

      if (this.expirationTime && new Date() < new Date(this.expirationTime)) {
        this.isSubscribed = true;
      } else {
        this.isSubscribed = false;
        // Do not clear local storage aggressively, just status
        if (localStorage.getItem("purchaseStatus") === 'purchased') {
             localStorage.setItem("purchaseStatus", "expired");
        }
      }
    },

    async validateAndGetData() {
      this.loading = true;
      this.loadingType = 'validating';

      // GA4: 开始恢复订阅
      if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'restore_purchase_start', {
          'method': 'email'
        });
      }

      try {
        await this.$refs.emailForm.validate();

        const email = this.formData.email;
        
        // Developer Backdoor (Preserved)
        if (email === 'emperinter#@outlook.com') {
            this.grantDevAccess(email);
            return;
        }

        this.loadingType = 'fetching';
        await this.fetchPaddleData(email);

      } catch (error) {
        if (error?.message) {
            console.warn("Validation or Fetch Error", error);
        }
      } finally {
        this.loading = false;
        this.loadingType = '';
      }
    },

    grantDevAccess(email) {
        const expirationTime = new Date();
        expirationTime.setDate(expirationTime.getDate() + 365);
        
        localStorage.setItem('purchaseStatus', 'purchased');
        localStorage.setItem('expirationTime', expirationTime.toISOString());
        localStorage.setItem('email', email);
        
        this.refreshLocalState();
        ElNotification({ title: 'Success', message: 'Developer Access Granted', type: 'success' });
        
        setTimeout(() => window.location.reload(), 1000);
    },

    async fetchPaddleData(email) {
      try {
        // 1. Get Customer
        const userRes = await axios.get(`/api/get_paddle_data/customer`, {
          params: { email, format: 'json' },
          headers: { 'Authorization': `Bearer ${config.token}` }
        });
        
        const userData = userRes.data.data?.[0];
        if (!userData) throw new Error("User not found");
        
        this.userInfo = userData;
        localStorage.setItem("email", userData.email);

        // 2. Get Transactions
        const txRes = await axios.get(`/api/get_paddle_data/transactions`, {
          params: { customer_id: userData.id, status: 'completed' },
          headers: { 'Authorization': `Bearer ${config.token}` }
        });

        this.transactionInfo = txRes.data.data || [];
        
        // 3. Determine Status
        const subTx = this.transactionInfo.find(t => t.subscription_id !== null);
        const oneTimeTx = this.transactionInfo.find(t => t.subscription_id == null);

        if (subTx) {
           await this.handleSubscription(subTx.subscription_id);
        } else if (oneTimeTx) {
           this.handleOneTimePurchase(oneTimeTx);
        } else {
           throw new Error("No active purchases found");
        }
        
        this.refreshLocalState();
        this.$emit('subscription-updated');

        // GA4: 恢复订阅成功
        if (typeof window.gtag !== 'undefined') {
          window.gtag('event', 'restore_purchase_success', {
            'product_name': this.productName,
            'purchase_type': subTx ? 'subscription' : 'one_time'
          });
        }

        ElNotification({ title: this.$t('Success'), message: this.$t('Purchase Restored'), type: 'success' });

      } catch (error) {
        console.error(error);

        // GA4: 恢复订阅失败
        if (typeof window.gtag !== 'undefined') {
          window.gtag('event', 'restore_purchase_failed', {
            'error_message': error?.message || 'Unknown error'
          });
        }

        ElNotification({
           title: this.$t("Warning"),
           message: this.$t("InvaildUser"), // Typo in original key preserved
           type: 'warning'
        });
      }
    },

    handleOneTimePurchase(tx) {
        this.productName = tx.items[0].price.name;
        // Logic from original: Billed Date + Product Days
        const billedAt = new Date(tx.billed_at);
        const days = productDaysMap[this.productName] || 0;
        
        const expDate = new Date(billedAt);
        expDate.setDate(expDate.getDate() + days);
        
        // Save
        localStorage.setItem("expirationTime", expDate.toISOString());
        localStorage.setItem("purchaseStatus", new Date() < expDate ? "purchased" : "expired");
    },

    async handleSubscription(subId) {
        const subRes = await axios.get(`/api/get_paddle_data/subscriptions`, {
            params: { sub_id: subId, format: 'json' },
            headers: { 'Authorization': `Bearer ${config.token}` }
        });
        
        const subData = subRes.data.data;
        this.productName = subData.items[0].price.name;
        
        // Only valid if ends_at > now ? Original logic used ends_at directly
        // "current_billing_period.ends_at"
        const endDate = new Date(subData.current_billing_period.ends_at);
        
        localStorage.setItem("expirationTime", endDate.toISOString());
        localStorage.setItem("purchaseStatus", new Date() < endDate ? "purchased" : "expired");
        
        this.cancel_url = subData.management_urls?.cancel;
        this.update_payment_method_url = subData.management_urls?.update_payment_method;
    }
  }
};
</script>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.5s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
